import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { VariantsBody } from '@/shared/api/generated/model';
import { shopSetVariants, shopVariants } from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';
import { combine, axisValues, variantLabel, type AxisDraft, type VariantDraft } from './shop';

const MAX_AXES = 3;

function draftsOf(body: VariantsBody): { axes: AxisDraft[]; rows: VariantDraft[] } {
  return {
    axes: (body.axes ?? []).map((axis) => ({
      name: axis.name,
      values: (axis.values ?? []).join(', '),
    })),
    rows: (body.variants ?? []).map((v) => ({
      ...(v.id === undefined ? {} : { id: v.id }),
      options: v.options,
      sku: v.sku ?? '',
      price: v.price === undefined ? '' : String(v.price),
      oldPrice: v.old_price === undefined ? '' : String(v.old_price),
      quantity: v.quantity === undefined ? '' : String(v.quantity),
      waiting: v.waiting ?? 0,
    })),
  };
}

/** Число из поля: пусто — undefined, не число — NaN (покажем ошибку). */
function numberOf(raw: string): number | undefined {
  const value = raw.trim().replace(',', '.');

  return value === '' ? undefined : Number(value);
}

/**
 * Варианты товара (шаг 9.6): оси — размер, цвет — и сочетания со своим
 * артикулом, ценой и остатком. Сохраняются сразу, без публикации: остаток
 * варианта меняет и каждый заказ. С вариантами в корзину кладётся вариант, а
 * остаток самого товара не используется.
 */
export function VariantsPanel({
  recordId,
  canWrite,
  onChanged,
}: {
  recordId: string;
  canWrite: boolean;
  onChanged: (hasVariants: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<{ axes: AxisDraft[]; rows: VariantDraft[] }>();
  const [busy, setBusy] = useState(false);

  const variants = useQuery({
    queryKey: ['variants', recordId],
    queryFn: async () => {
      const response = await shopVariants(recordId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос вариантов');
      }

      return response.data;
    },
  });

  if (variants.data === undefined) {
    return null;
  }

  const saved = draftsOf(variants.data);
  const editing = draft !== undefined;
  const { axes, rows } = draft ?? saved;
  const axisNames = axes.map((a) => ({ name: a.name.trim() }));

  const setAxes = (next: AxisDraft[]) => {
    setDraft({ axes: next, rows: combine(next, rows) });
  };

  const setRow = (index: number, patch: Partial<VariantDraft>) => {
    setDraft({ axes, rows: rows.map((row, i) => (i === index ? { ...row, ...patch } : row)) });
  };

  const save = () => {
    const body = {
      axes: axes
        .map((a) => ({ name: a.name.trim(), values: axisValues(a.values) }))
        .filter((a) => a.name !== '' && a.values.length > 0),
      variants: rows.map((row) => {
        const price = numberOf(row.price);
        const oldPrice = numberOf(row.oldPrice);
        const quantity = numberOf(row.quantity);

        return {
          ...(row.id === undefined ? {} : { id: row.id }),
          options: row.options,
          sku: row.sku.trim(),
          ...(price === undefined ? {} : { price }),
          ...(oldPrice === undefined ? {} : { old_price: oldPrice }),
          ...(quantity === undefined ? {} : { quantity }),
        };
      }),
    };

    const broken = body.variants.find(
      (v) =>
        (v.price !== undefined && !(v.price >= 0)) ||
        (v.old_price !== undefined && !(v.old_price >= 0)) ||
        (v.quantity !== undefined && !(Number.isInteger(v.quantity) && v.quantity >= 0)),
    );

    if (broken !== undefined) {
      toast.error('Цена — число от 0, остаток — целое число от 0 или пусто');

      return;
    }

    setBusy(true);

    shopSetVariants(recordId, body)
      .then((response) => {
        if (response.status === 200) {
          queryClient.setQueryData(['variants', recordId], response.data);
          onChanged((response.data.variants ?? []).length > 0);
        }

        setDraft(undefined);
        toast.success('Варианты сохранены');
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось сохранить варианты');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (!editing && rows.length === 0) {
    return canWrite ? (
      <section className={styles['panel']} aria-label="Варианты товара">
        <div className={styles['panel__head']}>
          <h2 className={styles['panel__title']}>Варианты</h2>
          <Badge size="sm" tone="neutral">
            нет
          </Badge>
          <div className={styles['panel__end']}>
            <Button
              size="sm"
              variant="outline"
              iconLeft={<Plus size={14} />}
              onClick={() => {
                setDraft({ axes: [{ name: 'Размер', values: '' }], rows: [] });
              }}
            >
              Добавить варианты
            </Button>
          </div>
        </div>
        <p className={styles['muted']}>
          Размер, цвет — у каждого сочетания свой артикул, цена и остаток. Без вариантов товар
          заказывают как есть.
        </p>
      </section>
    ) : null;
  }

  return (
    <section className={styles['panel']} aria-label="Варианты товара">
      <div className={styles['panel__head']}>
        <h2 className={styles['panel__title']}>Варианты</h2>
        <Badge size="sm" tone="neutral">
          {rows.length}
        </Badge>
        {canWrite && !editing && (
          <div className={styles['panel__end']}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setDraft(saved);
              }}
            >
              Изменить
            </Button>
          </div>
        )}
      </div>

      {editing && (
        <div className={styles['axes']}>
          {axes.map((axis, index) => (
            <div key={index} className={styles['axis']}>
              <Input
                aria-label="Название оси"
                placeholder="Размер"
                value={axis.name}
                onChange={(event) => {
                  setAxes(
                    axes.map((a, i) => (i === index ? { ...a, name: event.target.value } : a)),
                  );
                }}
              />
              <Input
                aria-label="Значения через запятую"
                placeholder="XS, S, M, L, XL"
                value={axis.values}
                onChange={(event) => {
                  setAxes(
                    axes.map((a, i) => (i === index ? { ...a, values: event.target.value } : a)),
                  );
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                aria-label="Убрать ось"
                onClick={() => {
                  setAxes(axes.filter((_, i) => i !== index));
                }}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          {axes.length < MAX_AXES && (
            <div>
              <Button
                size="sm"
                variant="ghost"
                iconLeft={<Plus size={14} />}
                onClick={() => {
                  setAxes([...axes, { name: axes.length === 0 ? 'Размер' : 'Цвет', values: '' }]);
                }}
              >
                Ещё ось
              </Button>
            </div>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className={styles['variants']} role="table" aria-label="Варианты">
          <div className={styles['variants__head']} role="row">
            <span role="columnheader">Вариант</span>
            <span role="columnheader">Артикул</span>
            <span role="columnheader">Цена</span>
            <span role="columnheader">Старая цена</span>
            <span role="columnheader">Остаток</span>
          </div>
          {rows.map((row, index) => (
            <div
              key={variantLabel(axisNames, row.options)}
              className={styles['variants__row']}
              role="row"
            >
              <span role="cell" className={styles['variants__label']}>
                {variantLabel(axisNames, row.options)}
                {!editing && (row.waiting ?? 0) > 0 && (
                  <Badge size="sm" tone="accent">
                    ждут: {row.waiting}
                  </Badge>
                )}
              </span>
              {editing ? (
                <>
                  <Input
                    mono
                    aria-label="Артикул"
                    value={row.sku}
                    onChange={(event) => {
                      setRow(index, { sku: event.target.value });
                    }}
                  />
                  <Input
                    inputMode="decimal"
                    aria-label="Цена"
                    placeholder="как у товара"
                    value={row.price}
                    onChange={(event) => {
                      setRow(index, { price: event.target.value });
                    }}
                  />
                  <Input
                    inputMode="decimal"
                    aria-label="Старая цена"
                    placeholder="как у товара"
                    value={row.oldPrice}
                    onChange={(event) => {
                      setRow(index, { oldPrice: event.target.value });
                    }}
                  />
                  <Input
                    inputMode="numeric"
                    aria-label="Остаток"
                    placeholder="не считается"
                    value={row.quantity}
                    onChange={(event) => {
                      setRow(index, { quantity: event.target.value });
                    }}
                  />
                </>
              ) : (
                <>
                  <span role="cell" className={styles['mono']}>
                    {row.sku || '—'}
                  </span>
                  <span role="cell">{row.price === '' ? 'как у товара' : row.price}</span>
                  <span role="cell">{row.oldPrice === '' ? '—' : row.oldPrice}</span>
                  <span role="cell">
                    {row.quantity === '' ? (
                      'не считается'
                    ) : row.quantity === '0' ? (
                      <Badge size="sm" tone="danger">
                        нет
                      </Badge>
                    ) : (
                      `${row.quantity} шт.`
                    )}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className={styles['panel__actions']}>
          <Button size="sm" loading={busy} onClick={save}>
            Сохранить варианты
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setDraft(undefined);
            }}
          >
            Отмена
          </Button>
          <span className={styles['muted']}>
            Сочетания собираются из осей сами. Пустая цена — как у товара, пустой остаток — не
            считается. Старая цена выше цены — на сайте скидка. Сохраняется сразу, без публикации.
          </span>
        </div>
      )}
    </section>
  );
}
